import React from "react";
import { withRouter, NavLink } from "react-router-dom";
import { flowRight as compose } from "lodash";
import { withStore } from "../../store";
import brightcovePlayerLoader from "@brightcove/player-loader";
import { ensureLongFormPluginsLoaded } from "./loadPlugins";
import { Button } from "@brightcove/studio-components";
import { SVGImage, SVG } from "../../assets/images";
import Grid from "../../components/Grid/Grid";
import { fetchHandler, getClientApiURL, getReportingEndpoint, isShortFormContent } from "../../utils";
import "./Player.scss";
import { metUrl } from "../../config/metMainSite";

let authzId;

const checkAuthorization = (
  onSuccess,
  onFailure,
  sessionId?,
  reference_id?
) => {
  let authzURL = "/authorization";
  if (sessionId) {
    authzURL = `${authzURL}/${sessionId}`;
  }
  let url = `${getClientApiURL(authzURL)}`;

  if (reference_id) {
    const newURL = new URL(url);
    newURL.searchParams.set("reference_id", reference_id);
    url = newURL.toString();
  }
  const options = {
    method: "GET",
  };
  fetchHandler(url, options, onSuccess, onFailure);
};

const checkEntitlement = (onSuccess, onFailure, extId) => {
  const url = `${getClientApiURL(`/authorization?reference_id=${extId}`)}`;
  const options = {
    method: "GET",
  };
  fetchHandler(url, options, onSuccess, onFailure);
};

class Player extends React.Component<any, any> {
  player;
  CONTINUITY_FREQUENCY = 10 * 1000;
  // Handle for the institutional/student seat-check heartbeat below. Stored so
  // it can be cleared on unmount and before re-creating; otherwise every mount
  // leaked a perpetual 10s /authorization interval that accumulated over time.
  authzContinuityInterval: any = null;
  constructor(props) {
    super(props);
    this.state = {
      entitled: undefined,
      paused: true,
    };
    this.playerRootRef = React.createRef<HTMLDivElement>();
  }
  playerRootRef: React.RefObject<HTMLDivElement>;

  // The videojs control bar (Play / Mute / Captions / Fullscreen / etc.) is
  // hidden via CSS for short_form_audio, but its buttons stay tab-reachable
  // and poison Tab order on the page. Mark the videojs root `inert` in that
  // mode so the hidden chrome is fully removed from focus + click + AT.
  syncInertOnPlayerRoot = () => {
    const root = this.playerRootRef.current;
    if (!root) return;
    const shouldBeInert = this.props.info.mediaType === "short_form_audio";
    if ((root as any).inert !== shouldBeInert) {
      (root as any).inert = shouldBeInert;
    }
  };

  componentDidMount() {
    this.syncInertOnPlayerRoot();
    const { user } = this.props.store;
    const isStudent = user && user.data && user.institutionName;
    if (!this.props.store.userFetchPending) {
      const $this = this;
      if (isStudent) {
        checkAuthorization(
          ({ session, status }) => {
            if (status && status === 429) {
              $this.props.history.replace("/user_limit");
            } else {
              authzId = session;
              $this.brightcovePlayer();
              // Clear any prior heartbeat before starting a new one so remounts
              // don't stack overlapping intervals.
              if ($this.authzContinuityInterval) {
                clearInterval($this.authzContinuityInterval);
              }
              $this.authzContinuityInterval = setInterval(() => {
                checkAuthorization(
                  undefined,
                  undefined,
                  authzId,
                  this.props.info.extId
                );
              }, $this.CONTINUITY_FREQUENCY);
            }
          },
          ({ status }) => {
            console.log("unhandled error in checkAuthz fetch");
          },
          authzId,
          this.props.info.extId
        );
      } else {
        checkEntitlement(
          () => this.setState({ entitled: true }),
          () => {
            this.setState({ entitled: false });
          },
          this.props.info.extId
        );
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.info.mediaType !== this.props.info.mediaType) {
      this.syncInertOnPlayerRoot();
    }
    if (
      !_.isEqual(
        prevProps.store.userFetchPending,
        this.props.store.userFetchPending
      ) ||
      prevProps.info.id !== this.props.info.id ||
      !_.isEqual(prevState.entitled, this.state.entitled)
    ) {
      if (this.player) {
        this.player.dispose();
      }
      this.brightcovePlayer();
    }
  }

  brightcovePlayer = () => {
    return loadBrightcovePlayer.call(this, this.props, this.state);
  };

  disposePlayer = () => {
    if (this.player) {
      const playerId = isShortFormContent(this.props.info.mediaType)
        ? process.env.REACT_APP_BRIGHTCOVE_PLAYER_ID_SHORT_FORM
        : process.env.REACT_APP_BRIGHTCOVE_PLAYER_ID_LONG_FORM;
      const playerStylesContainer = document.querySelector(
        `.bc-style-${playerId}-default`
      );

      brightcovePlayerLoader.reset();
      this.player.dispose();

      if (playerStylesContainer) {
        playerStylesContainer.remove();
      }
    }
  };

  togglePlay = () => {
    if (this.player) {
      const isPaused = this.player.paused();

      if (isPaused) {
        this.player.play();
      } else {
        this.player.pause();
      }

      this.setState({
        paused: !isPaused,
      });
    }
  };

  onVideoEnded = () => {
    this.setState({
      paused: true,
    });
  };

  componentWillUnmount() {
    if (this.authzContinuityInterval) {
      clearInterval(this.authzContinuityInterval);
      this.authzContinuityInterval = null;
    }
    this.disposePlayer();
  }

  // Add to MOoD Cart
  /*

    public enum SubscriptionTypeEnum

    {

        None = 0,

        Annual = 1,

        Monthly = 2,

        SixMonth = 3,

        Trial = 4,

        RentalAudio = 5,

        RentalVideo = 6,

        RentalHDVideo = 7,

        Comp = 9,

        Employee = 10,

        AnnualGift = 14,

        SpecialOffer = 100,

        YearlyMember = 35,

        RokuTrial = 47,

        RokuMonthly = 48,

        RokuAnnual = 49,

        SamsungTrial = 50,

        SamsungMonthly = 51,

        SamsungAnnual = 52,

        iOSInAppTrial = 58,

        iOSInAppMonthly = 59,

        iOSInAppAnnual = 60,

        RokuInAppTrial = 61,

        RokuInAppMonthly = 62,

        RokuInAppAnnual = 63,

        AmazonInAppTrial = 65,

        AmazonInAppMonthly = 66,

        AmazonInAppAnnual = 67,

        MetOperaOnDemand3MonthPromo = 69

    }



*/
  // https://metopera.org/MoodSubscribe/MoodSubscribe?subType=7&offerId=0&returnUrl=%2Fseason%2Fon-demand%2Fopera%2F%3Fupc%3D810004202993&videoUPC=810004202993&emptyCartIfTrue=False

  // much of the markup that is added here gets moved around/manipulated by the plugin itself. I don't believe this to be a sustainiable way of managing the player overlays. It should be revisisted in the future

  addRentalToCart = () => {
    const mediaType = this.props.performance.mediaType;
    const resolutionType = this.props.performance.resolutionType;
    const subType =
      mediaType === "VIDEO" ? (resolutionType === "HD" ? 7 : 6) : 5;
    const metoperaBaseUrl = process.env.REACT_APP_METOPERA_BASE_URL || "https://www.metopera.org";
    window.location = `${metoperaBaseUrl}/MoodSubscribe/MoodSubscribe?subType=${subType}&offerId=0&returnUrl=${encodeURIComponent(
      window.location
    )}&videoUPC=${this.props.performance.extId}&emptyCartIfTrue=False`;
  };

  addToCart = (purchaseType) => {
    const mediaType = this.props.performance.mediaType;
    const resolutionType = this.props.performance.resolutionType;
    const metoperaBaseUrl = process.env.REACT_APP_METOPERA_BASE_URL || "https://www.metopera.org";
    let subType;
    if (purchaseType === "rental") {
      subType = mediaType === "VIDEO" ? (resolutionType === "HD" ? 7 : 6) : 5;
    } else {
      subType = 4;
    }
    if (purchaseType === "trial") {
      window.location = `${metoperaBaseUrl}/season/on-demand/learn-more`;
    } else {
      window.location = `${metoperaBaseUrl}/MoodSubscribe/MoodSubscribe?subType=${subType}&offerId=0&returnUrl=${encodeURIComponent(
        window.location
      )}&videoUPC=${this.props.performance.extId}&emptyCartIfTrue=False`;
    }
  };

  render() {
    const {
      showLogIn,
      info,
      performance,
      endScreenInfo,
      history,
      store: { user },
    } = this.props;
    const { imageurl, header, mediaType } = info;
    const { entitled, paused } = this.state;
    const isStudent = user && user.data && user.institutionName;

    return (
      <div
        className={
          mediaType === "short_form_audio" ? `Player-wrapper ${mediaType}` : ""
        }
      >
        {mediaType === "short_form_audio" && (
          <button
            type="button"
            className="back-btn"
            onClick={() => history.push("/")}
            aria-label="Back to home"
          >
            <SVG.BackArrow aria-hidden="true" />
            <span>Back</span>
          </button>
        )}
        {mediaType === "short_form_audio" && (
          <div className="performance-info-wrapper">
            <div className="performance-image">
              <img src={imageurl} alt="" />
            </div>
            <div className="performance-info">
              {header && (
                <h2
                  className="performance-title"
                  dangerouslySetInnerHTML={{ __html: header }}
                />
              )}
              <div className="Player-controls">
                <button
                  type="button"
                  className="play-pause-btn"
                  onClick={this.togglePlay}
                  aria-label={paused ? "Play" : "Pause"}
                  aria-pressed={!paused}
                >
                  <img
                    src={paused ? SVGImage.PlayBtn : SVGImage.PauseBtn}
                    alt=""
                  />
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="Player" ref={this.playerRootRef}>
          {mediaType !== "short_form_audio" && (
            <div className="video-hover hidden-mobile">
              {mediaType !== "short_form_video" && (
                <NavLink to="/" exact className="video-hover-logo">
                  <SVG.MetLogo />
                </NavLink>
              )}
              <button
                type="button"
                className="video-hover-catalog back-btn"
                onClick={() => history.push("/")}
                aria-label="Back to home"
              >
                <SVG.BackArrow aria-hidden="true" />
                <span>Back</span>
              </button>
            </div>
          )}
          {!isShortFormContent(mediaType) && (
            <div
              className={`video-end player-overlay-end-screen hidden-mobile ${
                user && entitled ? "logged-in" : "logged-out"
              } `}
            >
              <div className="video-end-overlay"></div>
              {/* <Button theme="classic" Component="a" componentProps={{href: "https://www.metopera.org/calendar", target: "_blank"}} className="btn btn-transparent white buy-ticket-cta">Buy Tickets<SVG.Calendar /></Button> */}
              {!isStudent && (!user || (user && !entitled)) && (
                <div className="video-end-details">
                  <p className="player-overlay-title hidden-mobile">
                    <i>{info.header}</i> is only available with a<br />
                    Met Opera on Demand subscription or rental.
                  </p>
                  <Button
                    theme="classic"
                    className="btn btn-pink"
                    onClick={this.addToCart.bind(this, "trial")}
                  >
                    Start free 7-day trial
                  </Button>
                  <Button
                    theme="classic"
                    className="btn btn-transparent white"
                    onClick={this.addRentalToCart}
                  >
                    Rent for ${performance.rentalPrice}
                  </Button>
                  <p>
                    Already a Met Opera on Demand subscriber?{" "}
                    <button
                      type="button"
                      className="video-end-ask"
                      onClick={showLogIn}
                    >
                      Login
                    </button>
                  </p>
                  <Button
                    theme="classic"
                    Component="a"
                    componentProps={{
                      href: metUrl("/season/on-demand/Learn-More/"),
                      target: "_blank",
                    }}
                    className="btn btn-transparent white btn-learn-more"
                  >
                    Learn more<span> about on demand</span>
                  </Button>
                </div>
              )}
              {user && entitled && endScreenInfo.tiles.length > 0 && (
                <Grid {...endScreenInfo} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

function loadBrightcovePlayer(this: Player, props, state) {
  const {
    info: { id, extId, mediaType, position, tier },
    store: { user },
  } = this.props;
  const accountId = isShortFormContent(mediaType)
    ? process.env.REACT_APP_BRIGHTCOVE_ACCOUNT_ID_SHORT_FORM
    : process.env.REACT_APP_BRIGHTCOVE_ACCOUNT_ID_LONG_FORM;
  const playerId = isShortFormContent(mediaType)
    ? process.env.REACT_APP_BRIGHTCOVE_PLAYER_ID_SHORT_FORM
    : process.env.REACT_APP_BRIGHTCOVE_PLAYER_ID_LONG_FORM;
  const playerSettings = {
    refNode: ".Player",
    refNodeInsert: "append",
    accountId: accountId,
    playerId: playerId,
    embedId: "default",
    options: {
      id: "player",
    },
    onEmbedCreated: function (embed) {
      embed.removeAttribute("controls");
    },
  };

  const isStudent = user && user.data && user.institutionName;

  if (isShortFormContent(mediaType)) {
    playerSettings["videoId"] = `${extId}`;
  }

  return brightcovePlayerLoader(playerSettings)
    .then(async (success) => {
      let continuityInterval;
      this.player = success.ref;

      if (!isShortFormContent(mediaType)) {
        // Load the Met videoJS plugin bundle from this site's own origin.
        // Replaces the previous flow where Brightcove's compiled `_default`
        // embed had the plugin code baked into its index.min.js. The
        // unconfigured `default_default` embed contains only the videoJS
        // base, and the plugins live here in vendor/+src/player-plugins/
        // (built to public/met-player-plugins.js by tools/build-plugins.js).
        await ensureLongFormPluginsLoaded();

        this.player.MOoD({
          video: `ref:${extId}`,
          percentWatched: position,
          user: isStudent || (state.entitled && user) ? user : null,
          isStudent,
          entitled: state.entitled,
          tier,
          servicesEndpoint: getReportingEndpoint(),
        });

        const updateContinuity = () => {
          const time = this.player.currentTime(),
            complete = time / this.player.duration() > 0.98; // within 2% of end, call it complete

          if (!complete) {
            const url = `${getClientApiURL("/event")}`;
            const options = {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                eventType: "update-video-position",
                data: {
                  contentId: id,
                  position: Math.floor(time * 1000),
                },
              }),
            };
            fetchHandler(url, options);
          } else {
            onVideoEnded();
          }
        };

        const deleteContinuity = () => {
          const url = `${getClientApiURL("/event")}`;
          const options = {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              eventType: "delete-video-position",
              data: {
                contentId: id,
              },
            }),
          };
          fetchHandler(url, options);
        };

        const clearContinuityInterval = () => {
          continuityInterval && clearInterval(continuityInterval);
          continuityInterval = null;
        };

        const onVideoEnded = () => {
          deleteContinuity();
          clearContinuityInterval();
        };

        if (user && state.entitled) {
          this.player.one("canplay", () => {
            if (position) {
              this.player.currentTime(position / 1000);
            }
          });

          this.player.on("play", () => {
            continuityInterval = setInterval(function () {
              updateContinuity();
            }, this.CONTINUITY_FREQUENCY);
          });

          this.player.on("pause", () => {
            clearContinuityInterval();
            updateContinuity();
          });

          this.player.on("ended", onVideoEnded);

          this.player.on("dispose", function () {
            clearContinuityInterval();
          });
        }
      } else {
        this.player.on("ended", this.onVideoEnded);
      }
    })
    .catch((err) => {
      this.setState({
        error: true,
        errorMsg: "Player failed to load.",
      });
      console.error(err);
    });
}

export default compose(withRouter, withStore)(Player);
